// src/utils/lazyWithRetry.ts
// ────────────────────────────────────────────────────────────────────────────
// Resilient React.lazy() wrapper — survives stale-chunk errors after deploys.
//
// When a code-split chunk fails to load (new deploy invalidates old hashes),
// this retries the dynamic import up to `maxRetries` times.  If all retries
// fail, it forces a full page reload (once) so the browser fetches the new
// entry-point with fresh chunk manifests.
//
// A sessionStorage flag prevents infinite reload loops.
// ────────────────────────────────────────────────────────────────────────────
import { lazy, type ComponentType } from 'react';

const RELOAD_FLAG = 'pac_chunk_reload';

/**
 * Wrap a dynamic import factory with retry + hard-reload fallback.
 *
 * Usage:
 *   const MyComp = lazyWithRetry(() => import('./MyComp'));
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  maxRetries = 2,
) {
  return lazy(async () => {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const mod = await factory();
        // Successful load — clear the reload flag if it was set
        sessionStorage.removeItem(RELOAD_FLAG);
        return mod;
      } catch (err: any) {
        const isChunkError =
          err?.name === 'ChunkLoadError' ||
          /loading chunk .* failed/i.test(err?.message ?? '');

        if (!isChunkError) throw err; // unrelated error — propagate

        if (attempt < maxRetries) {
          // Brief backoff before retry (100ms, 300ms)
          await new Promise(r => setTimeout(r, 100 * (attempt + 1)));
          continue;
        }

        // All retries exhausted — force a full page reload (once)
        if (!sessionStorage.getItem(RELOAD_FLAG)) {
          sessionStorage.setItem(RELOAD_FLAG, '1');
          window.location.reload();
          // Return a never-resolving promise so React doesn't render stale UI
          return new Promise<never>(() => {});
        }

        // Already reloaded once — surface the error to the error boundary
        throw err;
      }
    }

    // TypeScript exhaustiveness — should never reach here
    throw new Error('lazyWithRetry: unexpected control flow');
  });
}
